// ── Framework core ──────────────────────────────────────────────────────────
import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import { startAnalytics } from "./utils/analytics";
import store from "./store";
import i18n from "./lang";

// ── Bootstrap utilities ──────────────────────────────────────────────────────
import "./utils/rem.js";
import "./icons";
import { otaDisplayName } from "./utils/otaLogo";
import { overrideConsole } from "./utils/overrideConsole";
import { installScoreFormat } from "./utils/scoreFormat";
import ModuleLoadState from "./components/common/ModuleLoadState.vue";
import { trackInputModality, kbdToggle, kbdButton } from "./utils/keyboardNav";

// ── UI library: Element UI ───────────────────────────────────────────────────
import ElementUI, { Notification as ElNotification } from "element-ui";
import "element-ui/lib/theme-chalk/index.css";
import "element-ui/lib/theme-chalk/icon.css";

// ── UI library: TDesign ──────────────────────────────────────────────────────
import TDesign from "tdesign-vue";
import "tdesign-vue/es/style/index.css";

// ── UI library: Swiper ───────────────────────────────────────────────────────
import Swiper from "r-swiper2";

// ── UI library: Toast ────────────────────────────────────────────────────────
import Toast from "vue-toastification";
import "@/assets/css/toast.css";
import "@/assets/css/icon-button.css";
import "@/assets/css/skeleton.css";
import "@/assets/css/adaptive-tokens.css";
import "@/assets/css/text-arrow.css";

// ── Map styles ───────────────────────────────────────────────────────────────
import "leaflet/dist/leaflet.css";

// ── Charting library ─────────────────────────────────────────────────────────
import * as echarts from "echarts";

// ── Console override — silences logs in production ───────────────────────────
overrideConsole();

// ── Plugin bootstrap ─────────────────────────────────────────────────────────
Vue.use(ElementUI);
Vue.use(TDesign);
// TDesign overrides Vue.prototype.$notify — restore ElementUI's Notification so
// any legacy $notify call uses the expected implementation instead of TDesign's.
Vue.prototype.$notify = ElNotification;
Vue.use(Swiper);

// lock-scroll is disabled per-instance on each el-dialog / el-drawer usage
// via :lock-scroll="false" to avoid body overflow:hidden layout shifts.

const options = {
  position: "top-center",
  timeout: 3000,
  closeOnClick: true,
  pauseOnHover: true,
  showProgressBar: true,
  maxToasts: 3,
  newestOnTop: false,
  transition: "bounce",
};
Vue.use(Toast, options);

// ── Global prototype assignments ─────────────────────────────────────────────
Vue.prototype.$echarts = echarts;
window.echarts = echarts;

// 页面显示用的分数格式化，见 utils/scoreFormat.js
installScoreFormat(Vue);
// 模块级加载状态（骨架 → 加载中… → 超时 / 失败重试），各页面直接用 <ModuleLoadState>
Vue.component("ModuleLoadState", ModuleLoadState);

// Format a Date object to YYYY-MM-DD using the local timezone
Vue.prototype.$formatLocalDate = (date) => {
  if (!(date instanceof Date)) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// 平台（OTA）的显示名：等价于 $t(`OTAs.${code}`)，唯一区别是 INTERNAL 会按当前酒店
// 所属集团改名（IHG → "Direct"，规则见 utils/otaLogo.js）。模板里显示平台名一律用它，
// 别再直接写 $t('OTAs.xxx')，否则 IHG 酒店那里会漏成 "Internal"。
// 必须是普通 function（要用组件的 this.$t / this.$store），在模板或 computed 里调用才会跟着切店更新。
Vue.prototype.$otaName = function (code) {
  return otaDisplayName(code, this.$t(`OTAs.${code}`), this.$store);
};

Vue.prototype.$noStaging = false;

// Global category filter — hotels show 7 categories, restaurants show their own
import { filterCategoryByVenue } from '@/utils/categoryFilter';
import { sanitizeHtml } from "@/utils/xss";
Vue.prototype.$filterCategoryByVenue = function(categories) {
  return filterCategoryByVenue(categories, this.$store.state.hotel.isHotel);
};

// Required by vue.config.js: all v-html compile to xss(_s(...))
Vue.prototype.xss = sanitizeHtml;

// Global event bus — used for cross-component communication
Vue.prototype.$bus = new Vue();

// 键盘操作：只在用键盘时显示焦点框（html.kbd-nav），div 做的切换 / 按钮能用键盘操作（utils/keyboardNav.js）
trackInputModality();
Vue.directive("kbd-toggle", kbdToggle);
Vue.directive("kbd-button", kbdButton);
startAnalytics(router);

// ── Vue config ────────────────────────────────────────────────────────────────
Vue.config.productionTip = false;

// ── App initialization ────────────────────────────────────────────────────────
new Vue({
  router,
  i18n,
  store,
  render: (h) => h(App),
}).$mount("#app");
